import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Send, FileText, Zap, Plus, Trash2, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, today, datePrefix, fmtDate } from "@/lib/api";

const round2 = (n) => Math.round(n * 100) / 100;

export const QuickInvoiceDialog = ({ client, draft, defaultTax, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (client) setF({
      items: [{ description: `${datePrefix(today())}${client.service_description || "Service"}`, quantity: 1, unit_price: client.default_amount ?? 0 }],
      tax: client.tax_rate ?? defaultTax, date: today(),
    });
  }, [client, defaultTax]);

  if (!client || !f) return null;
  const subtotal = round2(f.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0));
  const taxRate = draft ? draft.tax_rate : Number(f.tax) || 0;
  const taxAmt = round2((subtotal + (draft?.subtotal || 0)) * taxRate / 100);
  const total = round2(subtotal + (draft?.subtotal || 0) + taxAmt);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k) => (e) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)) });
  const addItem = () => setF({ ...f, items: [...f.items, { description: datePrefix(f.date), quantity: 1, unit_price: "" }] });
  const removeItem = (i) => setF({ ...f, items: f.items.filter((_, j) => j !== i) });

  const create = async (send) => {
    const items = f.items.filter((it) => it.description.trim());
    if (!items.length) return toast.error("Add at least one line item with a description");
    setBusy(send ? "send" : "draft");
    const line_items = items.map((it) => ({ description: it.description.trim(), quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 }));
    try {
      const { data } = draft
        ? await api.post(`/invoices/${draft.id}/lines`, { line_items, send_email: send })
        : await api.post("/invoices", { client_id: client.id, date: f.date, tax_rate: Number(f.tax) || 0, send_email: send, line_items });
      onCreated?.(data);
      onClose();
      if (data.email_error) {
        toast.warning(`Invoice ${data.invoice_number} saved as a draft, but email failed: ${data.email_error}`, { duration: 9000 });
        return navigate(`/drafts/${data.id}`);
      }
      if (send) {
        toast.success(`Invoice ${data.invoice_number} sent to ${data.sent_to}`);
        return navigate(`/invoices/${data.id}`);
      }
      toast.success(draft ? `Added to draft ${data.invoice_number} · now ${money(data.total)}` : `Draft ${data.invoice_number} started · ${money(data.total)}`,
        { action: { label: "Open draft", onClick: () => navigate(`/drafts/${data.id}`) } });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="quick-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Zap className="h-5 w-5 text-amber-500" fill="currentColor" /> Invoice {client.name}</DialogTitle>
          <DialogDescription>{draft ? "Today's charge will be added to this client's open draft." : "Review the job, add any extras, then save or email it."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {draft && (
            <div data-testid="quick-invoice-draft-notice" className="rounded-lg border border-emerald-200 bg-emerald-50/70 dark:bg-emerald-950/30 dark:border-emerald-800 px-3 py-2.5 text-sm flex items-start gap-2">
              <NotebookPen className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold">Open draft <span className="font-mono">{draft.invoice_number}</span> · {money(draft.total)} so far</div>
                <div className="text-xs text-muted-foreground">{draft.line_items.length} charge{draft.line_items.length === 1 ? "" : "s"} since {fmtDate(draft.date)}. Email Now sends the whole draft.</div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line items</Label>
              <button type="button" data-testid="invoice-add-line-item-button" onClick={addItem} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                <Plus className="h-3.5 w-3.5" /> Add item
              </button>
            </div>
            <div className="grid grid-cols-[1fr_56px_88px_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
              <span>Description</span><span>Qty</span><span>Price</span><span />
            </div>
            {f.items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_56px_88px_32px] gap-2 items-center" data-testid={`invoice-line-item-${i}`}>
                <Input data-testid={`invoice-line-description-${i}`} value={it.description} onChange={setItem(i, "description")} placeholder="Service or add-on" />
                <Input data-testid={`invoice-line-qty-${i}`} type="number" step="0.01" min="0" value={it.quantity} onChange={setItem(i, "quantity")} className="font-mono px-2" aria-label="Quantity" />
                <Input data-testid={`invoice-line-price-${i}`} type="number" step="0.01" min="0" value={it.unit_price} onChange={setItem(i, "unit_price")} placeholder="0.00" className="font-mono px-2" aria-label="Price" />
                <button type="button" data-testid={`invoice-line-remove-${i}`} onClick={() => removeItem(i)} disabled={f.items.length === 1}
                  className="h-8 w-8 grid place-items-center rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-30" aria-label="Remove item">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {!draft && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tax %</Label>
                <Input data-testid="invoice-form-tax-input" type="number" step="0.001" min="0" value={f.tax} onChange={set("tax")} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input data-testid="invoice-form-date-input" type="date" value={f.date} onChange={set("date")} />
              </div>
            </div>
          )}
          <div className="rounded-lg bg-secondary p-4 space-y-1 text-sm">
            {draft && <div className="flex justify-between"><span className="text-muted-foreground">Already on draft</span><span className="font-mono">{money(draft.subtotal)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">{draft ? "Today's charges" : "Subtotal"}</span><span className="font-mono" data-testid="invoice-form-subtotal">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Tax ({taxRate}%)</span><span className="font-mono">{money(taxAmt)}</span></div>
            <div className="flex justify-between items-baseline border-t border-border pt-2 mt-2">
              <span className="font-semibold">{draft ? "New draft total" : "Total"}</span>
              <span className="font-mono font-bold text-2xl text-emerald-600" data-testid="invoice-form-total">{money(total)}</span>
            </div>
          </div>
          {!client.email && <p className="text-xs text-amber-600 font-medium">This client has no email on file. Add one to send by email, or save as draft and download the PDF.</p>}
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="invoice-create-submit-button" variant="outline" disabled={!!busy} onClick={() => create(false)} className="h-12 font-bold">
              {draft ? <NotebookPen className="h-4 w-4 mr-2" /> : <FileText className="h-4 w-4 mr-2" />} {busy === "draft" ? "Saving..." : draft ? "Add to Draft" : "Save Draft"}
            </Button>
            <Button data-testid="invoice-create-and-send-button" disabled={!!busy || !client.email} onClick={() => create(true)} className="zap-btn h-12 rounded-lg">
              <Send className="h-4 w-4 mr-2" /> {busy === "send" ? "Sending..." : "Email Now"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{draft ? "The invoice is dated the day you email it. Manage all open drafts on the Drafts tab." : "Save Draft starts a running tab you can add charges to on later visits. Invoice number is assigned automatically."}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
